import React from "react";
import styled from "styled-components";
import { formatDate } from "@shared/utils/date";
import { getNewLookTokens, newLookType } from "common/NewLook/tokens.ts";
import { ICON_TYPES } from "@shared/common/constants";
import { PrecisionType, TradeExtraInfoActivity } from "@shared/types";
import TextWithPrecisionAndIcon from "common/Text/TextWithPrecisionAndIcon";

interface NewLookActivityProps {
  activity: TradeExtraInfoActivity;
  priceCoin?: string;
  color: string;
  /** Kept for callers; the new look colours the dot only. */
  textColor?: string;
  pricePrecision?: number;
  isLast?: boolean;
}

/**
 * New-look timeline entry: a coloured dot on a hairline rail, the event in
 * the figure role with its status as meta underneath, and the execution
 * price over the time on the right.
 */
const NewLookActivity = ({
  activity,
  priceCoin,
  color,
  pricePrecision,
  isLast,
}: NewLookActivityProps) => (
  <Item>
    <Rail $isLast={isLast}>
      <Dot style={{ borderColor: color }} />
    </Rail>
    <Body>
      <Main>
        <Label>{activity.description}</Label>
        <Status>{activity.status}</Status>
      </Main>
      <Side>
        <Price>
          {activity.execution_price ? (
            <TextWithPrecisionAndIcon
              text={activity.execution_price}
              precisionType={PrecisionType.PRICE}
              precisionCoin={priceCoin}
              precision={pricePrecision}
              iconType={ICON_TYPES.COIN}
              coinOrExchangeName={priceCoin}
              justify="flex-end"
            />
          ) : (
            "-"
          )}
        </Price>
        <Time>{formatDate(activity.relevant_time, true, true)}</Time>
      </Side>
    </Body>
  </Item>
);

export default React.memo(NewLookActivity);

const Item = styled.div`
  display: flex;
  gap: 14px;
  width: 100%;
`;
const Rail = styled.div<{ $isLast?: boolean }>`
  position: relative;
  flex: none;
  width: 12px;
  &::after {
    content: "";
    position: absolute;
    left: 5px;
    top: 30px;
    bottom: ${(p) => (p.$isLast ? "100%" : "-6px")};
    width: 2px;
    border-radius: 2px;
    background: ${(p) => getNewLookTokens(p.theme).hairline};
  }
`;
// The event's colour (entry, take-profit, stop, trade) lives on the dot only.
const Dot = styled.span`
  position: absolute;
  top: 16px;
  left: 0;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 3px solid;
  background: ${(p) => getNewLookTokens(p.theme).panel};
`;
const Body = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 12px 0 16px;
`;
const Main = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
`;
const Label = styled.span`
  ${newLookType.figure};
  color: ${(p) => getNewLookTokens(p.theme).ink};
`;
const Status = styled.div`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
`;
const Side = styled.div`
  flex: none;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  text-align: right;
`;
const Price = styled.div`
  ${newLookType.figure};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  * {
    font-size: inherit !important;
    font-weight: inherit !important;
  }
`;
const Time = styled.div`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
`;
